import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Linkedin } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { TEAM } from '../../data/team';

export default function TeamSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const navigate = useNavigate();
  const { lang } = useLanguage();

  // Parallax effects
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  const texts = {
    en: {
      tag: 'The Team',
      title: 'Meet the',
      titleAccent: 'innovators',
      sub: 'The minds transforming waste management through technology, AI, and open innovation.',
    },
    es: {
      tag: 'El Equipo',
      title: 'Conoce a los',
      titleAccent: 'innovadores',
      sub: 'Las mentes que transforman la gestión de residuos mediante tecnología, IA e innovación abierta.',
    },
  };

  const t = texts[lang] || texts.es;

  return (
    <section id="equipo" ref={ref} className="py-20 sm:py-32 relative overflow-hidden bg-gray-50 dark:bg-transparent">
      {/* Animated background */}
      <motion.div style={{ y, opacity }} className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: 'radial-gradient(circle, #8DC21F 0%, transparent 70%)' }} />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full blur-3xl opacity-10" style={{ background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)' }} />
      </motion.div>

      <div className="v-container relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <motion.span 
            className="v-tag green mb-6 inline-flex"
            animate={{ 
              boxShadow: [
                '0 0 0px rgba(141,194,31,0.2)',
                '0 0 25px rgba(141,194,31,0.4)',
                '0 0 0px rgba(141,194,31,0.2)',
              ]
            }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            {t.tag}
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white/95 mb-4 tracking-tight">
            {t.title}{' '}
            <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: '#8DC21F' }}>
              {t.titleAccent}
            </span>
          </h2>
          <p className="text-base sm:text-lg text-gray-700 dark:text-white/65 max-w-3xl mx-auto leading-relaxed">
            {t.sub}
          </p>
        </motion.div>

        {/* Team grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TEAM.map((member, i) => {
            const Icon = member.icon;
            const role = lang === 'en' ? member.roleEn : member.roleEs;
            const bio = lang === 'en' ? member.bioEn : member.bioEs;

            const card = (
              <motion.div
                initial={{ opacity: 0, y: 60, scale: 0.9 }}
                animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ 
                  duration: 0.8, 
                  delay: i * 0.1,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ 
                  y: -10, 
                  scale: 1.02,
                  transition: { duration: 0.3 }
                }}
                onClick={() => navigate(`/equipo/${member.slug}`)}
                className="liquid-glass rounded-3xl overflow-hidden group relative cursor-pointer"
              >
                {/* Gradient overlay on hover */}
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100"
                  style={{ background: `linear-gradient(135deg, ${member.color}08 0%, transparent 100%)` }}
                  transition={{ duration: 0.4 }}
                />

                {/* Photo placeholder (circular) */}
                <div className="relative h-48 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${member.color}15 0%, ${member.color}05 100%)` }}>
                  <motion.div
                    className="w-32 h-32 rounded-full flex items-center justify-center"
                    style={{ background: `${member.color}20`, border: `3px solid ${member.color}40` }}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    {member.photo ? (
                      <img src={member.photo} alt={`${member.name}, ${role}`} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <Icon size={48} style={{ color: member.color }} />
                    )}
                  </motion.div>

                  {/* LinkedIn badge */}
                  <motion.a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    whileHover={{ scale: 1.15, rotate: 5 }}
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 dark:bg-black/90 flex items-center justify-center shadow-lg"
                    style={{ border: `2px solid ${member.color}` }}
                    data-testid={`team-linkedin-${member.slug}`}
                  >
                    <Linkedin size={20} style={{ color: member.color }} />
                  </motion.a>
                </div>

                {/* Content */}
                <div className="p-6 relative z-10">
                  <h3 className="text-lg font-bold text-black dark:text-white/90 mb-1">{member.name}</h3>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: member.color }}>
                    {role}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-white/65 leading-relaxed">
                    {bio}
                  </p>
                </div>

                {/* Shine effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '100%' }}
                  transition={{ duration: 0.8 }}
                />
              </motion.div>
            );

            return (
              <div key={member.slug} data-testid={`team-card-${member.slug}`}>{card}</div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
